import { Plus, Trash2 } from 'lucide-react';
import { DatePicker } from '@components/date-picker';
import React from 'react';

import { FormSection } from '@components/form-section';
import { Select } from '@components/select';
import { StickyBar } from '@components/sticky-bar';
import { FieldError, FieldLabel } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import { Textarea } from '@components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@components/ui/tooltip';
import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import {
    ActivityType,
    CompositePointSourceType,
    WorkoutCategory,
    WorkoutCategoryWithTypesDto,
    WorkoutTypeV2
} from '@api/FitnessActivityClient';
import { CurrencySelect } from '@components/currency-select';

const toIsoDay = (value: Date | null) => {
    if (!value) {
        return '';
    }
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
};

const fromIsoDay = (value?: string) =>
    value ? new Date(`${value}T00:00:00`) : null;

const Field = ({
    label,
    error,
    helperText,
    children
}: {
    label: string;
    error?: string;
    helperText?: string;
    children: React.ReactNode;
}) => (
    <div className="flex w-full flex-col gap-1.5">
        <FieldLabel label={label} />
        {children}
        {helperText && !error && (
            <p className="text-xs text-muted-foreground">{helperText}</p>
        )}
        <FieldError error={error} />
    </div>
);

const enumOptions = (values: string[]) =>
    values.map((value) => ({ label: formatEnumLabel(value), value }));

const POINT_TYPE_KEY_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

function sanitizeSvg(raw: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(raw, 'image/svg+xml');
    doc.querySelectorAll('script, foreignObject').forEach((el) => el.remove());
    doc.querySelectorAll('*').forEach((el) => {
        for (const attr of Array.from(el.attributes)) {
            if (attr.name.startsWith('on')) el.removeAttribute(attr.name);
        }
    });
    return new XMLSerializer().serializeToString(doc.documentElement);
}

function formatEnumLabel(value: string): string {
    return value
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export interface RuleFormData {
    sourceType: CompositePointSourceType | '';
    sourceKey: string;
    workoutCategory: WorkoutCategory | '';
    workoutType: WorkoutTypeV2 | '';
    expression: string;
    priority: string;
    enabled: boolean;
}

export interface PointDefinitionFormData {
    pointTypeKey: string;
    label: string;
    symbolSvg: string;
    enabled: boolean;
    dayFrom: string;
    rules: RuleFormData[];
}

interface RuleFieldErrors {
    sourceType?: string;
    sourceKey?: string;
    workoutCategory?: string;
    workoutType?: string;
    expression?: string;
    priority?: string;
}

interface TopLevelFieldErrors {
    label?: string;
    symbolSvg?: string;
}

interface ValidationResult {
    formError?: string;
    pointTypeKeyError?: string;
    labelError?: string;
    fieldErrors: TopLevelFieldErrors;
    ruleErrors: RuleFieldErrors[];
}

interface PointDefinitionFormProps {
    title: string;
    submitLabel: string;
    submittingLabel: string;
    initialData?: PointDefinitionFormData;
    isSubmitting: boolean;
    onCancel(): void;
    onSubmit(data: PointDefinitionFormData): Promise<void>;
}

function emptyRule(): RuleFormData {
    return {
        sourceType: '',
        sourceKey: '',
        workoutCategory: '',
        workoutType: '',
        expression: '',
        priority: '0',
        enabled: true
    };
}

function defaultFormData(): PointDefinitionFormData {
    return {
        pointTypeKey: '',
        label: '',
        symbolSvg: '',
        enabled: true,
        dayFrom: '',
        rules: [emptyRule()]
    };
}

function validateForm(data: PointDefinitionFormData): ValidationResult {
    const ruleErrors: RuleFieldErrors[] = data.rules.map(() => ({}));

    if (!data.pointTypeKey.trim()) {
        return {
            formError: 'Point type key is required.',
            fieldErrors: {},
            ruleErrors
        };
    }

    if (!POINT_TYPE_KEY_REGEX.test(data.pointTypeKey)) {
        return {
            pointTypeKeyError:
                'Must start with a letter and use only letters, digits, and underscores.',
            fieldErrors: {},
            ruleErrors
        };
    }

    if (!data.label.trim()) {
        return {
            formError: 'Label is required.',
            labelError: 'Label is required.',
            fieldErrors: {},
            ruleErrors
        };
    }

    if (data.label.length > 255) {
        return {
            labelError: 'Label must not exceed 255 characters.',
            fieldErrors: {},
            ruleErrors
        };
    }

    if (data.rules.length === 0) {
        return {
            formError: 'At least one source rule is required.',
            fieldErrors: {},
            ruleErrors
        };
    }

    data.rules.forEach((rule, index) => {
        if (!rule.sourceType) {
            ruleErrors[index].sourceType = 'Source type is required.';
        }
        if (
            rule.sourceType === CompositePointSourceType.ACTIVITY_POINTS &&
            rule.sourceKey !== ActivityType.WORKOUT &&
            (rule.workoutCategory || rule.workoutType)
        ) {
            ruleErrors[index].workoutCategory =
                'Workout filters are available only when activity type is Workout.';
            ruleErrors[index].workoutType =
                'Workout filters are available only when activity type is Workout.';
        }
        if (!rule.expression.trim()) {
            ruleErrors[index].expression = 'Expression is required.';
        } else if (rule.expression.length > 256) {
            ruleErrors[index].expression =
                'Expression must not exceed 256 characters.';
        }
        const parsedPriority = parseInt(rule.priority, 10);
        if (rule.priority.trim() === '' || Number.isNaN(parsedPriority)) {
            ruleErrors[index].priority = 'Priority must be a valid integer.';
        }
    });

    const hasRuleErrors = ruleErrors.some((e) =>
        Boolean(
            e.sourceType ||
                e.sourceKey ||
                e.workoutCategory ||
                e.workoutType ||
                e.expression ||
                e.priority
        )
    );

    if (hasRuleErrors) {
        return {
            formError: 'Fix rule errors before saving.',
            fieldErrors: {},
            ruleErrors
        };
    }

    return { fieldErrors: {}, ruleErrors };
}

interface ParsedViolations {
    fieldErrors: TopLevelFieldErrors;
    ruleErrors: RuleFieldErrors[];
}

function parseViolations(error: unknown, ruleCount: number): ParsedViolations {
    const violations = (
        error as {
            response?: {
                data?: {
                    violations?: Array<{
                        field?: string;
                        message?: { defaultMessage?: string };
                    }>;
                };
            };
        }
    )?.response?.data?.violations;

    if (!violations) return { fieldErrors: {}, ruleErrors: [] };

    const fieldErrors: TopLevelFieldErrors = {};
    const ruleErrors: RuleFieldErrors[] = Array.from(
        { length: ruleCount },
        () => ({})
    );

    violations.forEach((v) => {
        if (!v.field) return;
        const msg = v.message?.defaultMessage ?? 'Invalid value';

        if (v.field === 'label' || v.field === 'symbolSvg') {
            fieldErrors[v.field] = msg;
            return;
        }

        const match = v.field.match(/^sourceRules\[(\d+)\]\.(\w+)$/);
        if (!match) return;
        const idx = parseInt(match[1], 10);
        const field = match[2] as keyof RuleFieldErrors;
        if (idx < ruleErrors.length) {
            ruleErrors[idx][field] = msg;
        }
    });

    return { fieldErrors, ruleErrors };
}

export function PointDefinitionForm({
    title,
    submitLabel,
    submittingLabel,
    initialData,
    isSubmitting,
    onCancel,
    onSubmit
}: PointDefinitionFormProps) {
    const [formData, setFormData] = React.useState<PointDefinitionFormData>(
        initialData ?? defaultFormData()
    );
    const [validation, setValidation] = React.useState<ValidationResult>({
        fieldErrors: {},
        ruleErrors: []
    });
    const [workoutCategoriesWithTypes, setWorkoutCategoriesWithTypes] =
        React.useState<WorkoutCategoryWithTypesDto[]>([]);

    const { fitnessActivityClient } = injectTSDI(Api);

    React.useEffect(() => {
        if (initialData) {
            setFormData(initialData);
        }
    }, [initialData]);

    React.useEffect(() => {
        let active = true;

        const fetchWorkoutCategories = async () => {
            try {
                const response =
                    await fitnessActivityClient.admin.workoutCategoryAdminControllerGetWorkoutCategories();

                if (active) {
                    setWorkoutCategoriesWithTypes(response.data || []);
                }
            } catch {
                if (active) {
                    setWorkoutCategoriesWithTypes([]);
                }
            }
        };

        void fetchWorkoutCategories();

        return () => {
            active = false;
        };
    }, [fitnessActivityClient]);

    const workoutTypesByCategory = React.useMemo(
        () =>
            workoutCategoriesWithTypes.reduce(
                (acc, current) => {
                    acc[current.category] = current.types || [];
                    return acc;
                },
                {} as Record<WorkoutCategory, WorkoutTypeV2[]>
            ),
        [workoutCategoriesWithTypes]
    );

    const workoutCategoryOptions = React.useMemo(() => {
        if (workoutCategoriesWithTypes.length > 0) {
            return workoutCategoriesWithTypes.map((item) => item.category);
        }

        return Object.values(WorkoutCategory);
    }, [workoutCategoriesWithTypes]);

    const allWorkoutTypeOptions = React.useMemo(() => {
        if (workoutCategoriesWithTypes.length > 0) {
            return Array.from(
                new Set(
                    workoutCategoriesWithTypes.flatMap(
                        (item) => item.types || []
                    )
                )
            );
        }

        return Object.values(WorkoutTypeV2);
    }, [workoutCategoriesWithTypes]);

    const getWorkoutTypeOptions = React.useCallback(
        (workoutCategory: WorkoutCategory | '') => {
            if (workoutCategory) {
                const categoryTypes = workoutTypesByCategory[workoutCategory];
                if (categoryTypes && categoryTypes.length > 0) {
                    return categoryTypes;
                }

                return Object.values(WorkoutTypeV2);
            }

            return allWorkoutTypeOptions;
        },
        [allWorkoutTypeOptions, workoutTypesByCategory]
    );

    React.useEffect(() => {
        setFormData((prev) => {
            let changed = false;

            const rules = prev.rules.map((rule) => {
                if (
                    rule.sourceType !==
                        CompositePointSourceType.ACTIVITY_POINTS ||
                    rule.sourceKey !== ActivityType.WORKOUT ||
                    !rule.workoutType
                ) {
                    return rule;
                }

                const workoutTypeOptions = getWorkoutTypeOptions(
                    rule.workoutCategory
                );

                if (workoutTypeOptions.includes(rule.workoutType)) {
                    return rule;
                }

                changed = true;
                return {
                    ...rule,
                    workoutType: '' as WorkoutTypeV2 | ''
                };
            });

            return changed ? { ...prev, rules } : prev;
        });
    }, [getWorkoutTypeOptions]);

    const setFieldValue = <K extends keyof PointDefinitionFormData>(
        field: K,
        value: PointDefinitionFormData[K]
    ) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const setRuleField = (
        index: number,
        field: keyof RuleFormData,
        value: string | boolean
    ) => {
        setFormData((prev) => ({
            ...prev,
            rules: prev.rules.map((rule, i) => {
                if (i !== index) return rule;
                if (field === 'sourceType' && value !== rule.sourceType) {
                    return {
                        ...rule,
                        sourceType: value as CompositePointSourceType | '',
                        sourceKey: '',
                        workoutCategory: '',
                        workoutType: ''
                    };
                }
                if (
                    field === 'sourceKey' &&
                    rule.sourceType === CompositePointSourceType.ACTIVITY_POINTS
                ) {
                    const nextSourceKey = value as string;

                    return {
                        ...rule,
                        sourceKey: nextSourceKey,
                        workoutCategory:
                            nextSourceKey === ActivityType.WORKOUT
                                ? rule.workoutCategory
                                : '',
                        workoutType:
                            nextSourceKey === ActivityType.WORKOUT
                                ? rule.workoutType
                                : ''
                    };
                }
                if (field === 'workoutCategory') {
                    return {
                        ...rule,
                        workoutCategory: value as WorkoutCategory | '',
                        workoutType: ''
                    };
                }
                return { ...rule, [field]: value };
            })
        }));
    };

    const addRule = () => {
        setFormData((prev) => ({
            ...prev,
            rules: [...prev.rules, emptyRule()]
        }));
    };

    const removeRule = (index: number) => {
        if (formData.rules.length <= 1) return;
        setFormData((prev) => ({
            ...prev,
            rules: prev.rules.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async () => {
        const result = validateForm(formData);
        setValidation(result);

        if (result.formError || result.pointTypeKeyError || result.labelError) {
            return;
        }

        try {
            await onSubmit(formData);
        } catch (e: unknown) {
            const { fieldErrors, ruleErrors } = parseViolations(
                e,
                formData.rules.length
            );
            const hasFieldErrors = Boolean(
                fieldErrors.label || fieldErrors.symbolSvg
            );
            const hasRuleErrors = ruleErrors.some(
                (re) =>
                    re.expression ||
                    re.sourceType ||
                    re.sourceKey ||
                    re.workoutCategory ||
                    re.workoutType ||
                    re.priority
            );
            if (hasFieldErrors || hasRuleErrors) {
                setValidation((prev) => ({
                    ...prev,
                    formError: 'Fix the errors highlighted below.',
                    labelError: fieldErrors.label,
                    fieldErrors,
                    ruleErrors
                }));
            }
        }
    };

    const sourceTypeOptions = enumOptions(
        Object.values(CompositePointSourceType)
    );
    const activityTypeOptions = [
        { label: 'Any (no filter)', value: '' },
        ...enumOptions(Object.values(ActivityType))
    ];
    const workoutCategorySelectOptions = [
        { label: 'All', value: '' },
        ...enumOptions(workoutCategoryOptions)
    ];

    return (
        <>
            <div className="mb-6 flex items-center justify-between gap-4">
                <h2 className="text-xl font-semibold tracking-tight">
                    {title}
                </h2>
            </div>

            <div className="flex max-w-[900px] flex-col gap-6 pb-24">
                {validation.formError && (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {validation.formError}
                        </AlertDescription>
                    </Alert>
                )}

                <FormSection title="Definition">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="flex items-center gap-2 md:pt-6">
                            <Switch
                                id="definition-enabled"
                                checked={formData.enabled}
                                onCheckedChange={(checked) =>
                                    setFieldValue('enabled', checked)
                                }
                            />
                            <Label htmlFor="definition-enabled">Enabled</Label>
                        </div>
                        <DatePicker
                            label="Day From"
                            clearable
                            value={fromIsoDay(formData.dayFrom)}
                            onChange={(next) =>
                                setFieldValue('dayFrom', toIsoDay(next))
                            }
                        />
                        <Field
                            label="Point Type Key"
                            error={validation.pointTypeKeyError}
                            helperText={
                                !formData.pointTypeKey
                                    ? 'e.g. LOTTO_POINT_01'
                                    : undefined
                            }
                        >
                            <Input
                                value={formData.pointTypeKey}
                                maxLength={255}
                                aria-invalid={Boolean(
                                    validation.pointTypeKeyError ||
                                        (validation.formError &&
                                            !formData.pointTypeKey.trim())
                                )}
                                onChange={(e) =>
                                    setFieldValue(
                                        'pointTypeKey',
                                        e.target.value
                                    )
                                }
                            />
                        </Field>
                        <Field
                            label="Label"
                            error={validation.labelError}
                            helperText="Human-readable display name"
                        >
                            <Input
                                value={formData.label}
                                maxLength={255}
                                aria-invalid={Boolean(validation.labelError)}
                                onChange={(e) =>
                                    setFieldValue('label', e.target.value)
                                }
                            />
                        </Field>
                    </div>
                </FormSection>

                <FormSection title="Symbol SVG">
                    <div className="grid gap-4 md:grid-cols-3">
                        <div className="md:col-span-2">
                            <Field
                                label="SVG Markup"
                                error={validation.fieldErrors.symbolSvg}
                                helperText={`${formData.symbolSvg.length}/10000 (optional)`}
                            >
                                <Textarea
                                    rows={4}
                                    placeholder="<svg>...</svg>"
                                    value={formData.symbolSvg}
                                    maxLength={10000}
                                    aria-invalid={Boolean(
                                        validation.fieldErrors.symbolSvg
                                    )}
                                    onChange={(e) =>
                                        setFieldValue(
                                            'symbolSvg',
                                            e.target.value
                                        )
                                    }
                                />
                            </Field>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <FieldLabel label="Preview" />
                            <div
                                className="flex min-h-20 items-center justify-center rounded-md border p-4 [&_svg]:max-h-20 [&_svg]:max-w-full"
                                dangerouslySetInnerHTML={
                                    formData.symbolSvg.trim()
                                        ? {
                                              __html: sanitizeSvg(
                                                  formData.symbolSvg
                                              )
                                          }
                                        : undefined
                                }
                            />
                        </div>
                    </div>
                </FormSection>

                <FormSection
                    title="Source Rules"
                    actions={
                        <Button variant="ghost" size="sm" onClick={addRule}>
                            <Plus />
                            Add Rule
                        </Button>
                    }
                >
                    <div className="flex flex-col gap-4">
                        {formData.rules.map((rule, index) => {
                            const ruleError =
                                validation.ruleErrors[index] ?? {};
                            const isCurrencyRule =
                                rule.sourceType ===
                                CompositePointSourceType.REWARD_CURRENCY;
                            const isActivityRule =
                                rule.sourceType ===
                                CompositePointSourceType.ACTIVITY_POINTS;
                            const isWorkoutActivityRule =
                                isActivityRule &&
                                rule.sourceKey === ActivityType.WORKOUT;
                            const workoutTypeOptions = getWorkoutTypeOptions(
                                rule.workoutCategory
                            );
                            const workoutFilterHint = !isWorkoutActivityRule
                                ? 'Available for Workout activity type only.'
                                : undefined;

                            return (
                                <div
                                    key={index}
                                    className="rounded-lg border p-4"
                                >
                                    <div className="mb-3 flex items-start justify-between">
                                        <p className="text-sm text-muted-foreground">
                                            Rule {index + 1}
                                        </p>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon-sm"
                                                        className="text-destructive"
                                                        onClick={() =>
                                                            removeRule(index)
                                                        }
                                                        disabled={
                                                            formData.rules
                                                                .length <= 1
                                                        }
                                                    >
                                                        <Trash2 />
                                                    </Button>
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Remove rule
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>

                                    <div className="grid gap-4 md:grid-cols-2">
                                        <Select
                                            label="Source Type"
                                            value={rule.sourceType}
                                            options={sourceTypeOptions}
                                            error={ruleError.sourceType}
                                            onChange={(value) =>
                                                setRuleField(
                                                    index,
                                                    'sourceType',
                                                    value
                                                )
                                            }
                                        />
                                        {isActivityRule ? (
                                            <Select
                                                label="Activity Type"
                                                value={rule.sourceKey}
                                                options={activityTypeOptions}
                                                error={ruleError.sourceKey}
                                                onChange={(value) =>
                                                    setRuleField(
                                                        index,
                                                        'sourceKey',
                                                        value
                                                    )
                                                }
                                            />
                                        ) : isCurrencyRule ? (
                                            <CurrencySelect
                                                value={rule.sourceKey}
                                                onChange={(code) =>
                                                    setRuleField(
                                                        index,
                                                        'sourceKey',
                                                        code
                                                    )
                                                }
                                                error={Boolean(
                                                    ruleError.sourceKey
                                                )}
                                                helperText={ruleError.sourceKey}
                                            />
                                        ) : (
                                            <Field
                                                label="Source Key"
                                                error={ruleError.sourceKey}
                                            >
                                                <Input
                                                    value={rule.sourceKey}
                                                    aria-invalid={Boolean(
                                                        ruleError.sourceKey
                                                    )}
                                                    onChange={(e) =>
                                                        setRuleField(
                                                            index,
                                                            'sourceKey',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Field>
                                        )}
                                        {isActivityRule && (
                                            <div className="flex flex-col gap-1.5">
                                                <Select
                                                    label="Workout Category"
                                                    value={rule.workoutCategory}
                                                    options={
                                                        workoutCategorySelectOptions
                                                    }
                                                    error={
                                                        ruleError.workoutCategory
                                                    }
                                                    disabled={
                                                        !isWorkoutActivityRule
                                                    }
                                                    onChange={(value) =>
                                                        setRuleField(
                                                            index,
                                                            'workoutCategory',
                                                            value
                                                        )
                                                    }
                                                />
                                                {!ruleError.workoutCategory &&
                                                    workoutFilterHint && (
                                                        <p className="text-xs text-muted-foreground">
                                                            {workoutFilterHint}
                                                        </p>
                                                    )}
                                            </div>
                                        )}
                                        {isActivityRule && (
                                            <div className="flex flex-col gap-1.5">
                                                <Select
                                                    label="Workout Type"
                                                    value={rule.workoutType}
                                                    options={[
                                                        {
                                                            label: 'All',
                                                            value: ''
                                                        },
                                                        ...enumOptions(
                                                            workoutTypeOptions
                                                        )
                                                    ]}
                                                    error={
                                                        ruleError.workoutType
                                                    }
                                                    disabled={
                                                        !isWorkoutActivityRule
                                                    }
                                                    onChange={(value) =>
                                                        setRuleField(
                                                            index,
                                                            'workoutType',
                                                            value
                                                        )
                                                    }
                                                />
                                                {!ruleError.workoutType &&
                                                    workoutFilterHint && (
                                                        <p className="text-xs text-muted-foreground">
                                                            {workoutFilterHint}
                                                        </p>
                                                    )}
                                            </div>
                                        )}
                                        <div className="md:col-span-2">
                                            <Field
                                                label="Expression"
                                                error={ruleError.expression}
                                                helperText={`${rule.expression.length}/256`}
                                            >
                                                <Input
                                                    placeholder="e.g. value * 10"
                                                    value={rule.expression}
                                                    maxLength={256}
                                                    aria-invalid={Boolean(
                                                        ruleError.expression
                                                    )}
                                                    onChange={(e) =>
                                                        setRuleField(
                                                            index,
                                                            'expression',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Field>
                                        </div>
                                        <Field
                                            label="Priority"
                                            error={ruleError.priority}
                                        >
                                            <Input
                                                type="number"
                                                value={rule.priority}
                                                aria-invalid={Boolean(
                                                    ruleError.priority
                                                )}
                                                onChange={(e) =>
                                                    setRuleField(
                                                        index,
                                                        'priority',
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </Field>
                                        <label className="flex cursor-pointer items-center gap-2 text-sm md:pt-6">
                                            <Checkbox
                                                checked={rule.enabled}
                                                onCheckedChange={(checked) =>
                                                    setRuleField(
                                                        index,
                                                        'enabled',
                                                        checked === true
                                                    )
                                                }
                                            />
                                            Rule Enabled
                                        </label>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </FormSection>
            </div>

            <StickyBar>
                <Button variant="ghost" onClick={onCancel}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? submittingLabel : submitLabel}
                </Button>
            </StickyBar>
        </>
    );
}
